import { ReactNode, useState } from 'react'

import {
  BillingPrice,
  BillingPriceInterval,
  BillingProduct,
} from '@tribeplatform/gql-client/types'
import { Link } from '@tribeplatform/react-components/Link'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { useUpgradePlanMessage } from '../../../../hooks/useUpgradePlanMessage.js'
import { useCurrentProductInformation } from '../hooks/useCurrentProductInformation.js'

import {
  PlanCardButtonLabel,
  type PlanCardButtonLabelVariant,
} from './PlanCardButtonLabel.js'
import { TrialConversionConfirmModal } from './TrialConversionConfirmModal.js'
import { usePlanCardButtonActions } from './usePlanCardButtonActions.js'

interface Props {
  newProduct: BillingProduct
  price: BillingPrice
  className?: string
  label?: ReactNode
}

export const PlanCardButton = ({
  newProduct,
  price,
  className,
  label,
}: Props) => {
  const { sendUpgradePlanMessage } = useUpgradePlanMessage()
  const {
    currentProduct,
    currentInterval,
    currentProductOrder,
    isManual,
    isTrial,
  } = useCurrentProductInformation()
  const { autoUpdatePlan, isLoading, openCheckout, switchBillingInterval } =
    usePlanCardButtonActions({
      currentProductOrder,
      newProduct,
      price,
    })

  const newProductOrder = newProduct.order
  const isTrialUpgradedPlan =
    isTrial && currentProduct?.upgradedPlanId === newProduct.id
  // Subscribing cancels the trial before Checkout (Stripe won't sell to a customer with an
  // active subscription), and an abandoned Checkout does not restore it. Confirm first so the
  // cancellation is never a surprise; only the dialog's Continue actually starts it.
  const [confirmOpen, setConfirmOpen] = useState(false)

  if (isTrialUpgradedPlan) {
    return (
      <>
        <Button
          fullWidth
          variant="primary"
          onClick={() => setConfirmOpen(true)}
          loading={isLoading}
          className={className}
        >
          {label ?? <PlanCardButtonLabel variant="subscribe" />}
        </Button>
        <TrialConversionConfirmModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false)
            openCheckout()
          }}
          isLoading={isLoading}
          newProduct={newProduct}
          price={price}
        />
      </>
    )
  }

  if (
    currentProduct?.id === newProduct?.id &&
    currentInterval === price?.interval
  ) {
    return (
      <Button fullWidth variant="secondary" disabled className={className}>
        {label ?? <PlanCardButtonLabel variant="current" />}
      </Button>
    )
  }

  if (currentProduct?.isEnterprise || newProduct.isEnterprise || isManual) {
    return (
      <Button
        fullWidth
        as={Link}
        href="https://bettermode.com/contact-sales"
        variant="primary"
        className={className}
      >
        {label ?? <PlanCardButtonLabel variant="contact" />}
      </Button>
    )
  }

  if (
    currentProduct &&
    currentInterval &&
    price?.interval !== currentInterval
  ) {
    if (newProduct?.id === currentProduct?.id) {
      const labelVariant: PlanCardButtonLabelVariant =
        price?.interval === BillingPriceInterval.Month
          ? 'switch-monthly'
          : 'switch-yearly'

      return (
        <Button
          fullWidth
          variant="primary"
          onClick={() => switchBillingInterval()}
          loading={isLoading}
          className={className}
        >
          {label ?? <PlanCardButtonLabel variant={labelVariant} />}
        </Button>
      )
    }
    if (newProductOrder < currentProductOrder) {
      if (!newProduct.downgradable) {
        return (
          <Button
            fullWidth
            variant="primary"
            onClick={() => sendUpgradePlanMessage(newProduct?.name)}
            loading={isLoading}
            className={className}
          >
            {label ?? <PlanCardButtonLabel variant="contact" />}
          </Button>
        )
      }

      const labelVariant: PlanCardButtonLabelVariant =
        price?.interval === BillingPriceInterval.Month
          ? 'downgrade-switch-monthly'
          : 'downgrade-switch-yearly'

      return (
        <Button
          fullWidth
          variant="primary"
          onClick={async () => {
            await switchBillingInterval({ sendToast: false })
            await autoUpdatePlan()
          }}
          loading={isLoading}
          className={className}
        >
          {label ?? <PlanCardButtonLabel variant={labelVariant} />}
        </Button>
      )
    }

    const labelVariant: PlanCardButtonLabelVariant =
      price?.interval === BillingPriceInterval.Month
        ? 'upgrade-switch-monthly'
        : 'upgrade-switch-yearly'

    return (
      <Button
        fullWidth
        variant="primary"
        onClick={async () => {
          await switchBillingInterval({ sendToast: false })
          await autoUpdatePlan()
        }}
        loading={isLoading}
        className={className}
      >
        {label ?? <PlanCardButtonLabel variant={labelVariant} />}
      </Button>
    )
  }

  if (newProductOrder < currentProductOrder) {
    return (
      <Button
        fullWidth
        variant="primary"
        onClick={() => {
          if (newProduct.downgradable) {
            autoUpdatePlan()
          } else {
            sendUpgradePlanMessage(newProduct?.name)
          }
        }}
        loading={isLoading}
        className={className}
      >
        {label ?? (
          <PlanCardButtonLabel
            variant={newProduct.downgradable ? 'downgrade' : 'contact'}
          />
        )}
      </Button>
    )
  }

  return (
    <>
      <Button
        fullWidth
        variant="primary"
        onClick={() => {
          if (currentProduct) {
            autoUpdatePlan()
          } else if (isTrial) {
            // A trialing network can reach this branch when its current product does not resolve,
            // and `openCheckout` cancels the trial subscription server-side before redirecting.
            // Confirm first so the cancellation is never a surprise — the same guard as the
            // "Subscribe now" path above.
            setConfirmOpen(true)
          } else {
            openCheckout()
          }
        }}
        loading={isLoading}
        className={className}
      >
        {label ?? <PlanCardButtonLabel variant="upgrade" />}
      </Button>
      {isTrial && (
        <TrialConversionConfirmModal
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false)
            openCheckout()
          }}
          isLoading={isLoading}
          newProduct={newProduct}
          price={price}
        />
      )}
    </>
  )
}
